import { useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { FileText, FileTextIcon, Files } from "@phosphor-icons/react";
import { ArrowUpRight, ChevronRight } from "assets/icons";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { DashboardBlock, DashboardBlockHeader, DashboardBlockContent } from "../layout/DashboardBlock";
import { BlockLoadingState, BlockEmptyState } from "../states/BlockStates";
import { blockStyles } from "../utils/blockStyles.utils";

interface DocumentsBlockNewProps {
  sharePointFolderId: string;
  sharePointFolderUrl: string;
}

/**
 * Documents Block (Draaiboeken)
 * Displays documents from SharePoint, limited to 8 items
 */
export const DocumentsBlockNew = ({
  sharePointFolderId,
  sharePointFolderUrl,
}: DocumentsBlockNewProps) => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { session } = useSession();

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!sharePointFolderId) return;
      
      setIsLoading(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/graph/folder/${sharePointFolderId}/files`,
          { method: "GET" },
          session,
        );
        const data = await response.json();
        setDocuments(data);
      } catch (error) {
        console.error("Error fetching documents:", error);
        setDocuments([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [sharePointFolderId, session]);

  // Limit to 8 documents
  const displayedDocuments = documents.slice(0, 8);

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<FileTextIcon weight="duotone" size={20} />}
        title="Draaiboeken"
        action={
          <div className="flex items-center divide-x divide-neutral-primary/20">
            <Link
              to="/draaiboeken"
              className={`${blockStyles.link.variants.primary} pr-3 ${blockStyles.link.withIcon}`}
            >
              Alle documenten <ArrowUpRight className="h-[18px] w-[18px]" />
            </Link>
            <a
              href={sharePointFolderUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`${blockStyles.link.variants.primary} pl-3 ${blockStyles.link.withIcon}`}
            >
              SharePoint <ArrowUpRight className="h-[18px] w-[18px]" />
            </a>
          </div>
        }
      />
      
      {isLoading ? (
        <DashboardBlockContent padding="none">
          <BlockLoadingState message="Documenten aan het ophalen..." />
        </DashboardBlockContent>
      ) : displayedDocuments.length > 0 ? (
        <DashboardBlockContent padding="none" className="relative overflow-hidden">
          <>
            <div className="relative z-1 flex flex-col">
              {displayedDocuments.map((doc: any) => (
                <div
                  key={doc.id}
                  onClick={() => window.open(doc.url, "_blank")}
                  className="group flex w-full cursor-pointer items-center justify-between p-3 hover:bg-moss-primary/5 transition-colors border-b border-neutral-primary/5 last:border-b-0"
                >
                  <p className="font-medium text-sm text-foreground truncate mr-2">{doc.name}</p>
                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                </div>
              ))}
            </div>
            {documents.length > 8 && (
              <div className="text-center p-3 border-t border-border">
                <Link 
                  to="/draaiboeken"
                  className={blockStyles.link.variants.primary}
                >
                  + {documents.length - 8} meer documenten bekijken →
                </Link>
              </div>
            )}
            <div className="absolute bottom-0 right-0 z-0 translate-x-1/4 translate-y-1/4 text-moss-primary/5 pointer-events-none">
              <Files weight="duotone" size={120} />
            </div>
          </>
        </DashboardBlockContent>
      ) : (
        <DashboardBlockContent padding="none">
          <BlockEmptyState
            icon={<FileText weight="duotone" size={48} />}
            title="Geen documenten gevonden"
            description="Er zijn momenteel geen draaiboeken beschikbaar"
          />
        </DashboardBlockContent>
      )}
    </DashboardBlock>
  );
};
